import React from 'react';
import PropTypes from 'prop-types';
import { withSize } from 'react-sizeme';

import Row from 'zignal-landscape/lib/Row';
import Col from 'zignal-landscape/lib/Col';
import Loading from 'zignal-landscape/lib/Loading';
import GridContent from 'zignal-landscape/lib/GridContent';
import TimeSeriesChart from 'zignal-landscape/lib/TimeSeriesChart';
import Button from 'zignal-landscape/lib/Button';
import TabbedPanes from 'zignal-landscape/lib/TabbedPanes';
import Tabs from 'zignal-landscape/lib/Tabs';
import Tab from 'zignal-landscape/lib/Tab';
import themeable from 'zignal-landscape/lib/themeable';
import { transformForExport } from './helpers';
import FilterButtonGroup from './components/FilterButtonGroup';
import DownloadMenu from '../../../../components/DownloadMenu/DownloadMenu';
import { formatChartData } from './helpers';
import {
  convertTimeStringToMoment,
  convertMomentToApiTime,
  convertTimestringToFormat,
  convertDatePickerTimeToMoment
} from '../../../../helpers/timeFormat';

const HistoricsLineChart = ({
  styles,
  data,
  isFetching,
  lastSearch,
  intervalOptions,
  onSearch,
  intervalSelectionIndex,
  selectInterval,
  colors,
  searchTerms,
  onSearchNew,
  filters,
  filterTerm,
  selectedSource,
  onShowTrendingModal,
  onToggleAnalysisModal,
  timezone,
  searchFormStartDate,
  searchFormEndDate,
  lineChartBackDisabled
}) => {

  const { milliseconds, intervalFormat } = intervalOptions[intervalSelectionIndex] || {};

  const handleOnChangeTab = (ind) => {
    if (intervalSelectionIndex !== ind) {
      selectInterval(ind);
      onSearch();
    }
  };

  const handleOnClickLineChart = (obj) => {
    const dateIntervalFormat = convertTimestringToFormat(obj.point.timestamp, intervalFormat, timezone);
    const targetDateAsMoment = convertTimeStringToMoment(dateIntervalFormat, intervalFormat, timezone);
    const startDate = convertMomentToApiTime(targetDateAsMoment);
    const endDate = convertMomentToApiTime(targetDateAsMoment.add(milliseconds, 'ms'));
    onSearchNew(startDate, endDate, false);
  };

  const handleOnClickBackButton = () => {
    const startDateAsMoment = convertDatePickerTimeToMoment(searchFormStartDate, timezone);
    const endDateAsMoment = convertDatePickerTimeToMoment(searchFormEndDate, timezone);
    const startDate = convertMomentToApiTime(startDateAsMoment);
    const endDate = convertMomentToApiTime(endDateAsMoment);
    onSearchNew(startDate, endDate, true);
  };

  const handleOnClickFilter = (term, index) => {
    filterTerm(index);
  };

  const getDataOrDefault = () => {
    if (lastSearch.length >= 1 && data.some(e => e.total > 0)) {
      return (
        <div className={styles.historicsLineChartContainer}>
          <div className={styles.LineChartOptions} key={'options'}>
            <div className={styles.TabContainer} style={{width: intervalOptions.length * 70}}>
              <TabbedPanes
                selectedIndex={intervalSelectionIndex}
                onChange={handleOnChangeTab}
              >
                <Tabs>
                  {intervalOptions.map(({ label }) => (
                    <Tab key={label}>{label}</Tab>
                  ))}
                </Tabs>
              </TabbedPanes>
            </div>
            <div className={styles.exportMenuContainer}>
              <DownloadMenu
                getAuthorsAndStories={() => transformForExport(data, intervalOptions[intervalSelectionIndex])}
                getData={() => transformForExport(data, intervalOptions[intervalSelectionIndex])}
                fileNameWithoutExtension="discover"
                exportFileName=""
                viewBy=""
                mfeMode
              />
            </div>
          </div>
          <div style={{height: 400, paddingTop: 25, backgroundColor: '#2F3E54', boxSizing: 'content-box'}}>
            <div className={styles.lineChartBackButtonContainer}>
              <Button
                onClick={handleOnClickBackButton}
                buttonStyle="secondary"
                disabled={lineChartBackDisabled}
              >
                <div className={styles.lineChartBackButton}>
                  <i className="fas fa-caret-left" />
                  <span>Back</span>
                </div>
              </Button>
            </div>
            <TimeSeriesChart
              yAxis={{
                title: {
                  text: 'Mentions',
                  tooltipText: 'Mentions'
                }
              }}
              line
              data={formatChartData(data.filter((value, index) => !filters.includes(index)), selectedSource)}
              showLegend={false}
              toolTipType="point"
              onPointClick={handleOnClickLineChart}
            />
          </div>
          <FilterButtonGroup
            key={'filters'}
            data={data}
            terms={searchTerms}
            colorMap={colors}
            filters={filters}
            onClick={handleOnClickFilter}
            selectedSource={selectedSource}
            onShowTrendingModal={onShowTrendingModal}
            onToggleAnalysisModal={onToggleAnalysisModal}
          />
        </div>);
    }
    return null;
  };

  if (isFetching) {
    return (
      <Row>
        <Col xs={12}>
          <GridContent>
            <div className={styles.HistoricsLineChartLight}>
              <Loading />
            </div>
          </GridContent>
        </Col>
      </Row>
    );
  }

  const chart = getDataOrDefault();

  if (chart) {
    return (
      <Row>
        <Col xs={12}>
          <GridContent>
            <div className={styles.HistoricsLineChartLight}>
              {chart}
            </div>
          </GridContent>
        </Col>
      </Row>
    );
  }

  return null;
};

HistoricsLineChart.propTypes = {
  styles: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  isFetching: PropTypes.bool,
  lastSearch: PropTypes.array.isRequired,
  intervalOptions: PropTypes.array.isRequired,
  intervalSelectionIndex: PropTypes.number.isRequired,
  selectInterval: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  colors: PropTypes.array.isRequired,
  searchTerms: PropTypes.array,
  onSearchNew: PropTypes.func,
  filters: PropTypes.array,
  filterTerm: PropTypes.func.isRequired,
  selectedSource: PropTypes.string,
  onShowTrendingModal: PropTypes.func,
  onToggleAnalysisModal: PropTypes.func,
  timezone: PropTypes.string,
  searchFormStartDate: PropTypes.string,
  searchFormEndDate: PropTypes.string,
  lineChartBackDisabled: PropTypes.bool,
};

export default withSize({monitorWidth: true, refreshRate: 200})(themeable(HistoricsLineChart));
